<template>
  <v-container fluid>
    <v-row dense>
      <v-col cols="12">

        <Dialog
        :stateId="dialogId"
        :maxWidth="DialogWidth.SimpleWide"
        @dialog-closing="closePreview"
        @close="closePreview">
          <TractHudPreview v-if="previewOpen" :sections="sections" @close="closePreview"/>
        </Dialog>

        <v-row>
          <v-col style="max-width: 800px;">
            <v-row align="center" no-gutters>
              <v-icon color="primary" class="mr-2">mdi-map-marker-outline</v-icon>
              <span class="text-h6 font-weight-regular">{{ $t('tractHud') }}</span>
              <v-spacer/>
              <Icon
              icon="mdi-eye-outline"
              :tooltipText="$t('preview')"
              @icon-clicked="openPreview"
              :small="false"
              margin="mr-4"
              elementId="tract-hud-preview-open"/>
              <Icon
              icon="mdi-restore"
              :tooltipText="$t('defaultOrder')"
              @icon-clicked="restoreDefaultOrder"
              :small="false"
              :disabled="!isContractManager || isDefaultOrder || saving"
              margin="mr-4"
              elementId="tract-hud-default-order"/>
            </v-row>
            <div class="text-caption d-flex align-center">
              {{ $t('tractHudInfo') }}
              <Icon
              v-if="isContractManager"
              icon="mdi-information-outline"
              iconColor="info"
              margin="ml-1"
              class="lh-0"
              :tooltipText="$t('dragAndDropToReorder')"/>
            </div>

            <v-skeleton-loader
            v-if="loading"
            type="table-row-divider@3"
            class="mt-3"/>

            <template v-else>
              <v-row dense class="mt-3">
                <v-col cols="12">
                  <v-sheet outlined rounded class="px-3 py-2 d-flex align-center grey--text">
                    <v-icon small class="mr-1" color="grey">mdi-lock</v-icon>
                    <span class="text-caption font-weight-bold hud-rank">{{ LOCKED_ROW_COUNT }}</span>
                    <span class="d-flex align-center hud-name">
                      <v-icon small class="mr-2" color="grey">mdi-format-title</v-icon>
                      <span class="text-subtitle-2">{{ $t('tractName') }}</span>
                    </span>
                    <v-spacer/>
                    <span class="text-caption">{{ $t('alwaysShown') }}</span>
                  </v-sheet>
                </v-col>
              </v-row>
              <draggable
              :value="sections"
              :disabled="!isContractManager || saving"
              animation=400
              swapThreshold=".75"
              tag="v-row"
              :component-data="{ props: { dense: true } }"
              @input="persist">
                <v-col cols="12" v-for="(item, position) in sections" :key="item.section" v-show="sectionFor(item)">
                  <v-sheet
                  outlined
                  rounded
                  class="px-3 py-2 d-flex align-center"
                  :style="isContractManager ? 'cursor: move;' : undefined">
                    <v-icon v-if="isContractManager" small class="mr-1">mdi-drag-vertical</v-icon>
                    <span class="text-caption font-weight-bold hud-rank">{{ rank(position) }}</span>
                    <span class="d-flex align-center hud-name">
                      <v-icon small class="mr-2">{{ sectionFor(item)?.icon }}</v-icon>
                      <span class="text-subtitle-2">{{ sectionFor(item)?.name }}</span>
                    </span>
                    <v-spacer/>
                    <v-simple-checkbox
                    :value="item.isVisible"
                    :disabled="!isContractManager || saving"
                    color="success"
                    :ripple="false"
                    @input="toggleVisibility(item)"/>
                  </v-sheet>
                </v-col>
              </draggable>
            </template>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import draggable from 'vuedraggable'
import { mapActions } from 'vuex'
import { TractHudSection, UserClaims } from '@/utils/Enumerations.js'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { DialogWidth } from '@/utils/Dialog.js'

const LOCKED_ROW_COUNT = 1

export default {
  name: 'TractHudSettings',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    TractHudPreview: () => import('@/components/settings-components/tract-settings/tract-hud/TractHudPreview.vue'),
    draggable
  },

  data: () => ({
    sections: [],
    loading: false,
    saving: false,
    previewOpen: false,
    dialogId: uniqueDialogId('tract-hud-preview-dialog'),
    DialogWidth,
    LOCKED_ROW_COUNT
  }),

  computed: {
    isContractManager () {
      return userAssignedClaim(UserClaims.ContractManager)
    },

    isDefaultOrder () {
      return this.sections.filter(this.sectionFor).every((item, index) => item.section === TractHudSection.enums[index]?.value)
    }
  },

  async created () {
    this.loading = true
    try {
      const sections = await this.fetchTractHudSections()
      this.sections = sections?.length ? sections : TractHudSection.defaultLayout()
    } finally {
      this.loading = false
    }
  },

  methods: {
    ...mapActions('tract-hud', ['fetchTractHudSections', 'updateTractHudSections']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    sectionFor ({ section }) {
      return TractHudSection.forInt(section)
    },

    rank (position) {
      return LOCKED_ROW_COUNT + this.sections.slice(0, position).filter(this.sectionFor).length + 1
    },

    toggleVisibility (item) {
      this.persist(this.sections.map(s => s.section === item.section ? { ...s, isVisible: !s.isVisible } : s))
    },

    restoreDefaultOrder () {
      const order = TractHudSection.enums.map(section => section.value)
      const position = ({ section }) => order.includes(section) ? order.indexOf(section) : order.length
      this.persist([...this.sections].sort((a, b) => position(a) - position(b)))
    },

    async persist (next) {
      if (!this.isContractManager) return
      const previous = this.sections
      this.sections = next
      this.saving = true
      try {
        this.sections = await this.updateTractHudSections(next.map(({ section, isVisible }) => ({ section, isVisible })))
        this.setSnack(this.$t('tractHudUpdated'))
      } catch (e) {
        this.sections = previous
        this.setSnackError(this.$t('tractHudUpdateFailed'))
      } finally {
        this.saving = false
      }
    },

    openPreview () {
      this.previewOpen = true
      this.openOrUpdateDialog(this.dialogId)
    },

    closePreview () {
      this.previewOpen = false
      this.closeDialogsAtOrAbove(this.dialogId)
    }
  }
}
</script>

<style scoped>
.hud-rank {
  flex: 0 0 1.5rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.hud-name {
  flex: 0 0 12rem;
  min-width: 0;
  padding-left: 0.75rem;
}
</style>
